import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { PaginationBar } from '@/components/ui/PaginationBar';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

export type MiniprogramScopeItem = {
  id: number;
  public_id: string;
  name: string;
  enabled: boolean;
  owner_username?: string | null;
};

type ListResp = {
  ok: boolean;
  items: MiniprogramScopeItem[];
  total: number;
  page: number;
  per_page: number;
};

const PER_PAGE = 20;

type Props = {
  /** URL 查询参数名，默认 mp_id */
  paramKey?: string;
  className?: string;
};

/**
 * 顶部小程序切换条：管理员可见归属账号；普通用户仅能看到自己的小程序（list_miniprograms 已过滤）。
 */
export function MiniprogramScopeBar({ paramKey = 'mp_id', className }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { role, loading: sessLoading } = useSession();
  const selectedId = Math.max(0, parseInt(searchParams.get(paramKey) || '0', 10) || 0);

  const [items, setItems] = useState<MiniprogramScopeItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [appliedQ, setAppliedQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch<ListResp>('list_miniprograms', {
        method: 'GET',
        query: { page, per_page: PER_PAGE },
      });
      if (r.ok) {
        setItems(r.items);
        setTotal(r.total);
      } else {
        setItems([]);
        setTotal(0);
      }
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    if (sessLoading) return;
    void load();
  }, [sessLoading, load]);

  const filteredItems = useMemo(() => {
    const q = appliedQ.trim().toLowerCase();
    if (!q) return items;
    return items.filter((m) => {
      const hay = `${m.name} ${m.public_id} #${m.id} ${m.owner_username ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, appliedQ]);

  const [selectedFallback, setSelectedFallback] = useState<MiniprogramScopeItem | null>(null);

  const selected = useMemo(() => {
    const inList = items.find((m) => m.id === selectedId) ?? filteredItems.find((m) => m.id === selectedId);
    if (inList) return inList;
    if (selectedFallback?.id === selectedId) return selectedFallback;
    return undefined;
  }, [items, filteredItems, selectedId, selectedFallback]);

  useEffect(() => {
    if (selectedId < 1) {
      setSelectedFallback(null);
      return;
    }
    if (items.some((m) => m.id === selectedId)) {
      setSelectedFallback(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const r = await apiFetch<{ ok: boolean; miniprogram?: { id: number; public_id: string; name: string; enabled: boolean } }>(
        'get_miniprogram',
        { method: 'GET', query: { id: selectedId } },
      );
      if (cancelled || !r.ok || !r.miniprogram) return;
      setSelectedFallback({
        id: r.miniprogram.id,
        public_id: r.miniprogram.public_id,
        name: r.miniprogram.name,
        enabled: r.miniprogram.enabled,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedId, items]);

  useEffect(() => {
    if (sessLoading || loading || selectedId > 0) return;
    const first = items[0];
    if (!first) return;
    const next = new URLSearchParams(searchParams);
    next.set(paramKey, String(first.id));
    navigate({ search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
  }, [sessLoading, loading, selectedId, items, searchParams, paramKey, navigate]);

  function setSelected(mpId: number) {
    const next = new URLSearchParams(searchParams);
    if (mpId > 0) {
      next.set(paramKey, String(mpId));
    } else {
      next.delete(paramKey);
    }
    navigate({ search: next.toString() ? `?${next.toString()}` : '' }, { replace: true });
  }

  function runSearch() {
    setAppliedQ(keyword.trim());
    setPage(1);
  }

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const isAdmin = role === 'admin';

  return (
    <Card title="当前小程序" className={className}>
      {loading ? (
        <Spinner label="加载小程序列表" />
      ) : items.length === 0 ? (
        <p className="text-sm text-amber-800">暂无可用小程序，请先在「小程序管理」中创建并关联站点。</p>
      ) : (
        <>
          <div className="mb-3 flex flex-col gap-3 md:flex-row md:flex-wrap md:items-end">
            <label className={`${labelClass} min-w-[14rem] flex-1`}>
              <span>切换小程序</span>
              <select
                className={inputClass}
                value={selectedId}
                onChange={(e) => setSelected(Math.max(0, parseInt(e.target.value, 10) || 0))}
              >
                <option value={0} disabled>
                  请选择
                </option>
                {selected && !filteredItems.some((m) => m.id === selected.id) ? (
                  <option value={selected.id}>
                    {selected.name}
                    {!selected.enabled ? '（已停用）' : ''} · #{selected.id}
                  </option>
                ) : null}
                {filteredItems.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                    {!m.enabled ? '（已停用）' : ''} · #{m.id}
                    {isAdmin && m.owner_username ? ` · ${m.owner_username}` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className={`${labelClass} min-w-[10rem] flex-1`}>
              <span>筛选名称 / 编号</span>
              <input
                type="search"
                className={inputClass}
                placeholder="本页内筛选"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
              />
            </label>
            <Button type="button" variant="secondary" onClick={runSearch}>
              查询
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setKeyword('');
                setAppliedQ('');
              }}
            >
              重置
            </Button>
          </div>
          {selected ? (
            <p className="text-sm text-slate-600">
              已选：<span className="font-medium text-slate-900">{selected.name}</span>
              <span className="ml-1.5 font-mono text-xs text-slate-500">
                #{selected.id} · {selected.public_id}
              </span>
              {isAdmin && selected.owner_username ? (
                <span className="ml-2 text-xs text-slate-500">归属 {selected.owner_username}</span>
              ) : null}
              {!selected.enabled ? <span className="ml-2 text-xs text-amber-700">（小程序已停用）</span> : null}
            </p>
          ) : (
            <p className="text-sm text-slate-600">请从上方选择要管理机器人的小程序。</p>
          )}
          {isAdmin ? (
            <p className="mt-2 text-xs text-slate-500">系统管理员可查看全部用户的小程序；机器人列表按小程序归属账号隔离展示。</p>
          ) : (
            <p className="mt-2 text-xs text-slate-500">仅显示您账号下的小程序与机器人。</p>
          )}
          {total > PER_PAGE ? (
            <PaginationBar
              className="mt-4"
              page={page}
              totalPages={totalPages}
              total={total}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
            />
          ) : null}
        </>
      )}
    </Card>
  );
}

export function useMiniprogramScopeId(paramKey = 'mp_id'): number {
  const [searchParams] = useSearchParams();
  return Math.max(0, parseInt(searchParams.get(paramKey) || '0', 10) || 0);
}
